import React from "react";

import { Home2026FeaturedWorkBlock as Value } from "@reactivated";

import { SectionHeader } from "../../shared/SectionHeader";
import { LogoTicker } from "../LogoTicker";
import { Card } from "./Card";
import { CaseStudyCursor } from "./CaseStudyCursor";
import { Gradient } from "./Gradient";

import styles from "./index.module.scss";

const DESKTOP_QUERY = "(min-width: 1024px)";

/** `el`'s layout offset from `ancestor`, ignoring transforms */
const offsetTopWithin = (el: HTMLElement, ancestor: HTMLElement) => {
    let top = 0;
    let node: HTMLElement | null = el;
    while (node && node !== ancestor) {
        top += node.offsetTop;
        const parent: Element | null = node.offsetParent;
        node = parent instanceof HTMLElement ? parent : null;
    }
    return top;
};

/**
 * Side by side, the Tula card's top is the headline's last line, however
 * many lines it breaks onto. Written as --fw-c1, which Elliman, the ticker
 * and the section's height are placed from. Stacked, the cards are in flow.
 */
const useHeadlineAnchor = (
    innerRef: React.RefObject<HTMLDivElement | null>,
) => {
    React.useLayoutEffect(() => {
        const inner = innerRef.current;
        const headline = inner?.querySelector("h2");
        if (!inner || !headline) return;
        let cancelled = false;

        // Layout offsets, not getBoundingClientRect: the header slides the
        // headline itself while it is armed
        const align = () => {
            if (cancelled) return;
            if (!window.matchMedia(DESKTOP_QUERY).matches) {
                inner.style.removeProperty("--fw-c1");
                return;
            }
            const bottom =
                offsetTopWithin(headline, inner) + headline.offsetHeight;
            inner.style.setProperty("--fw-c1", `${bottom.toFixed(1)}px`);
        };

        align();
        const observer = new ResizeObserver(align);
        observer.observe(headline);
        window.addEventListener("resize", align);
        void document.fonts.ready.then(align);
        return () => {
            cancelled = true;
            observer.disconnect();
            window.removeEventListener("resize", align);
        };
    }, [innerRef]);
};

/**
 * The 2026 homepage's Featured Work section (#37224): the section header,
 * three case study cards staggered down the grid and the client logo ticker,
 * over the section's cool gradient.
 */
export const Home2026FeaturedWorkBlock = (props: Value) => {
    const sectionRef = React.useRef<HTMLElement>(null);
    const innerRef = React.useRef<HTMLDivElement>(null);
    const [cursorOn, setCursorOn] = React.useState(false);
    useHeadlineAnchor(innerRef);

    const onImageEnter = React.useCallback(() => setCursorOn(true), []);
    const onImageLeave = React.useCallback(() => setCursorOn(false), []);

    return (
        <section ref={sectionRef} className={styles.root}>
            <Gradient className={styles.gradient} sectionRef={sectionRef} />
            <div ref={innerRef} className={styles.inner}>
                {props.header && (
                    <SectionHeader
                        value={props.header}
                        align="left"
                        className={styles.header}
                    />
                )}
                {props.case_studies?.slice(0, 3).map(
                    ({ id, value }, index) =>
                        // A deleted case study is dropped server-side, so the
                        // later cards move up a slot; a non-case-study page
                        // comes through as null and renders nothing
                        value && (
                            <Card
                                key={id}
                                value={value}
                                index={index}
                                onImageEnter={onImageEnter}
                                onImageLeave={onImageLeave}
                            />
                        ),
                )}
                <LogoTicker logos={props.logos} className={styles.ticker} />
            </div>
            <CaseStudyCursor active={cursorOn} />
        </section>
    );
};
